// src/dashboard/publicDailyStatus.js  (make sure path + casing match your imports)

import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../global/supabase';
import MayoLogo from '../assets/images/Mayo.png';
import '../global/styles.css';

function PublicDailyStatus() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  // Auto-refresh countdown (seconds)
  const REFRESH_SECONDS = 60;
  const [countdown, setCountdown] = useState(REFRESH_SECONDS);
  const [isMobile, setIsMobile] = useState(false);

  // Dispatch links/actions
  const dispatchTeamsUrl =
    'https://teams.microsoft.com/l/chat/19:ddb39939bff548119ec3ae9cf43b523e@thread.v2/conversations?context=%7B%22contextType%22%3A%22chat%22%7D';
  const dispatchPhone = '(904) 956-1820';
  const dispatchTel = 'tel:+19049561820';

  // PCS modal
  const [showPCS, setShowPCS] = useState(false);
  const [pcs, setPcs] = useState({
    isEmergency: false,
    bedConfined: false,
    cannotSitUpright: false,
    needsOxygen: false,
    needsCardiacMonitoring: false,
    needsIVMedsOrDrips: false,
    needsAirwaySupport: false,
    unsafeAltTransport: false,
    requiresTwoPersonAssist: false,
    alteredMentalStatus: false
  });

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    fetchLatestStatus();

    const interval = setInterval(() => {
      fetchLatestStatus();
      setCountdown(REFRESH_SECONDS);
    }, REFRESH_SECONDS * 1000);

    const countdownInterval = setInterval(() => {
      setCountdown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => {
      clearInterval(interval);
      clearInterval(countdownInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLatestStatus = async () => {
    try {
      const { data, error } = await supabase
        .from('daily_status')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) throw error;
      if (data && data.length > 0) setStatus(data[0]);
    } catch (err) {
      console.error('Error fetching status:', err);
    } finally {
      setLoading(false);
    }
  };

  const getCensusColor = (level) => {
    const colors = {
      Green: '#28a745',
      Yellow: '#ffc107',
      Red: '#dc3545',
      Purple: '#6f42c1',
      Black: '#343a40'
    };
    return colors[level] || '#6c757d';
  };

  const formatShortDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getRelativeUpdatedText = (dateString) => {
    if (!dateString) return '';
    const updatedAt = new Date(dateString).getTime();
    const now = Date.now();
    const diffMs = Math.max(0, now - updatedAt);
    const diffMin = Math.floor(diffMs / 60000);

    if (diffMin < 1) return 'Updated just now';
    if (diffMin === 1) return 'Updated 1 minute ago';
    if (diffMin < 60) return `Updated ${diffMin} minutes ago`;

    const diffHr = Math.floor(diffMin / 60);
    if (diffHr === 1) return 'Updated 1 hour ago';
    if (diffHr < 24) return `Updated ${diffHr} hours ago`;

    const diffDay = Math.floor(diffHr / 24);
    if (diffDay === 1) return 'Updated 1 day ago';
    return `Updated ${diffDay} days ago`;
  };

  const getFreshnessColor = (dateString) => {
    if (!dateString) return '#6c757d';
    const updatedAt = new Date(dateString).getTime();
    const diffMin = Math.floor((Date.now() - updatedAt) / 60000);

    if (diffMin <= 5) return '#1b5e20';
    if (diffMin <= 15) return '#8a6d00';
    return '#842029';
  };

  const parseShiftCounts = (value) => {
    if (!value || typeof value !== 'string') return null;

    const blsMatch = value.match(/BLS:\s*(\d+)/i);
    const alsMatch = value.match(/ALS:\s*(\d+)/i);

    const bls = blsMatch ? Number(blsMatch[1]) : null;
    const als = alsMatch ? Number(alsMatch[1]) : null;

    if (bls === null && als === null) return null;
    return { bls: bls ?? 0, als: als ?? 0 };
  };

  // Mileage thresholds (1-4 ambulances)
  const mileageTable = useMemo(
    () => ({
      greenYellow: { 1: 100, 2: 175, 3: 225, 4: 250 },
      red: { 1: 75, 2: 130, 3: 170, 4: 190 },
      purple: { 1: 55, 2: 100, 3: 125, 4: 140 },
      black: { 1: 40, 2: 75, 3: 95, 4: 100 }
    }),
    []
  );

  const getMileageFor = (censusLevel, ambulanceCount) => {
    const n = Math.max(1, Math.min(4, Number(ambulanceCount || 1)));
    const lvl = (censusLevel || '').toLowerCase();

    if (lvl === 'green' || lvl === 'yellow') return mileageTable.greenYellow[n];
    if (lvl === 'red') return mileageTable.red[n];
    if (lvl === 'purple') return mileageTable.purple[n];
    if (lvl === 'black') return mileageTable.black[n];
    return null;
  };

  const getMileageRowLabel = (censusLevel) => {
    const lvl = (censusLevel || '').toLowerCase();
    if (lvl === 'green' || lvl === 'yellow') return 'Green/Yellow';
    if (lvl === 'red') return 'Red';
    if (lvl === 'purple') return 'Purple';
    if (lvl === 'black') return 'Black';
    return '—';
  };

  const shifts = useMemo(() => {
    if (!status) return [];

    const s7a = parseShiftCounts(status.shift_7a_7p_units);
    const s11a = parseShiftCounts(status.shift_11a_11p_units);
    const s7p = parseShiftCounts(status.shift_7p_7a_units);

    const total7a = s7a ? s7a.bls + s7a.als : null;
    const total11a = s11a ? s11a.bls + s11a.als : null;
    const total7p = s7p ? s7p.bls + s7p.als : null;

    const combined11aTotal = total11a !== null ? (total11a || 0) + (total7a || 0) : null;

    return [
      { key: '7a', label: '7a - 7p', raw: status.shift_7a_7p_units, parsed: s7a, totalAmbulances: total7a },
      {
        key: '11a',
        label: '11a - 11p (combined with 7a-7p)',
        raw: status.shift_11a_11p_units,
        parsed: s11a,
        totalAmbulances: combined11aTotal
      },
      { key: '7p', label: '7p - 7a', raw: status.shift_7p_7a_units, parsed: s7p, totalAmbulances: total7p }
    ];
  }, [status]);

  const censusThresholdCards = useMemo(() => {
    const lvl = (status?.census_level || '').toLowerCase();
    const label = getMileageRowLabel(status?.census_level);

    const row =
      lvl === 'green' || lvl === 'yellow'
        ? mileageTable.greenYellow
        : lvl === 'red'
        ? mileageTable.red
        : lvl === 'purple'
        ? mileageTable.purple
        : lvl === 'black'
        ? mileageTable.black
        : null;

    if (!row) return null;
    return { label, row };
  }, [status, mileageTable]);

  const pcsResult = useMemo(() => {
    if (!showPCS) return null;

    if (pcs.isEmergency) {
      return {
        level: 'red',
        title: 'Emergency transport',
        summary:
          'If this is an emergency, use emergency activation procedures. This screening tool is for non-emergent discharge/IFT decision support only.'
      };
    }

    const strongIndicators =
      (pcs.needsAirwaySupport ? 1 : 0) +
      (pcs.needsIVMedsOrDrips ? 1 : 0) +
      (pcs.needsCardiacMonitoring ? 1 : 0) +
      (pcs.needsOxygen ? 1 : 0) +
      (pcs.cannotSitUpright ? 1 : 0);

    const functionalIndicators =
      (pcs.bedConfined ? 1 : 0) +
      (pcs.requiresTwoPersonAssist ? 1 : 0) +
      (pcs.alteredMentalStatus ? 1 : 0) +
      (pcs.unsafeAltTransport ? 1 : 0);

    if (
      strongIndicators >= 1 &&
      (pcs.unsafeAltTransport || pcs.cannotSitUpright || pcs.bedConfined)
    ) {
      return {
        level: 'green',
        title: 'Ambulance likely supported',
        summary:
          'Clinical needs and/or positioning/monitoring requirements suggest ambulance-level transport may be appropriate. Ensure documentation clearly explains why other transport is contraindicated.'
      };
    }

    if (strongIndicators >= 1 || functionalIndicators >= 2) {
      return {
        level: 'yellow',
        title: 'May be supported with stronger documentation',
        summary:
          'Some factors support ambulance use, but coverage often depends on clearly documenting why wheelchair van/POV is not safe or feasible, and what skilled services are required en route.'
      };
    }

    return {
      level: 'gray',
      title: 'Ambulance may be unlikely',
      summary:
        'Based on the selected answers, alternative transport may be appropriate. If ambulance is still requested, document specific clinical risks and contraindications to non-ambulance transport.'
    };
  }, [pcs, showPCS]);

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: '#f8f9fa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <img src={MayoLogo} alt="Mayo Clinic" style={{ width: 120, marginBottom: 20 }} />
          <p style={{ color: '#666' }}>Loading status...</p>
        </div>
      </div>
    );
  }

  if (!status) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: '#f8f9fa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 20
        }}
      >
        <div
          style={{
            background: 'white',
            borderRadius: 15,
            padding: 40,
            maxWidth: 600,
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
          }}
        >
          <img src={MayoLogo} alt="Mayo Clinic" style={{ width: 120, marginBottom: 20 }} />
          <h1 style={{ color: '#333', fontSize: 24, marginBottom: 10 }}>Daily Status</h1>
          <p style={{ color: '#666' }}>No status updates available yet.</p>
        </div>
      </div>
    );
  }

  const updatedText = getRelativeUpdatedText(status.created_at);
  const updatedColor = getFreshnessColor(status.created_at);

  const buttonBase = {
    width: '100%',
    padding: '14px 14px',
    borderRadius: 12,
    border: 'none',
    color: 'white',
    fontSize: 15,
    fontWeight: 800,
    cursor: 'pointer',
    boxShadow: '0 6px 18px rgba(0, 0, 0, 0.12)'
  };

  const buttonTeams = { ...buttonBase, background: '#0E3293' };
  const buttonCall = { ...buttonBase, background: '#1b5e20' };
  const buttonPCS = { ...buttonBase, background: '#B45309' };

  return (
    <>
      <div
        style={{
          minHeight: '100vh',
          background: '#f8f9fa',
          padding: 20,
          paddingTop: 'max(20px, env(safe-area-inset-top))',
          paddingBottom: 'max(20px, env(safe-area-inset-bottom))'
        }}
      >
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          {/* Sticky header (mobile) */}
          <div
            style={{
              position: isMobile ? 'sticky' : 'static',
              top: isMobile ? 'max(0px, env(safe-area-inset-top))' : 'auto',
              zIndex: 5000,
              background: isMobile ? '#f8f9fa' : 'transparent',
              paddingTop: isMobile ? 6 : 0,
              paddingBottom: isMobile ? 10 : 0
            }}
          >
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: isMobile ? 10 : 30 }}>
              <img
                src={MayoLogo}
                alt="Mayo Clinic"
                style={{
                  width: isMobile ? 88 : 120,
                  marginBottom: isMobile ? 10 : 20
                }}
              />

              <h1
                style={{
                  color: '#0E3293',
                  fontSize: isMobile ? 24 : 36,
                  fontWeight: 600,
                  marginBottom: 10
                }}
              >
                Daily Operational Status
              </h1>

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 14px',
                    borderRadius: 999,
                    background: '#eef3fb',
                    border: '1px solid #d7e3ff',
                    color: '#0E3293',
                    fontSize: 13,
                    fontWeight: 700
                  }}
                >
                  <span style={{ color: updatedColor }}>{updatedText}</span>
                  <span style={{ opacity: 0.7, fontWeight: 600 }}>
                    ({formatShortDateTime(status.created_at)})
                  </span>
                </div>

                <div style={{ fontSize: 12, color: '#666' }}>
                  Refreshing in <strong>{countdown}s</strong>
                  <span style={{ marginLeft: 8, color: '#999' }}>
                    • By: {status.updated_by_first_name} {status.updated_by_last_name}
                  </span>
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                gap: 10,
                marginBottom: isMobile ? 10 : 16
              }}
            >
              <button
                onClick={() => window.open(dispatchTeamsUrl, '_blank', 'noopener,noreferrer')}
                style={buttonTeams}
              >
                💬 Message Dispatch (Teams)
              </button>

              <button
                onClick={() => {
                  window.location.href = dispatchTel;
                }}
                style={buttonCall}
              >
                📞 Call Dispatch {dispatchPhone}
              </button>

              <button onClick={() => setShowPCS(true)} style={buttonPCS}>
                🧾 Ambulance Qualification (PCS)
              </button>
            </div>
          </div>

          {/* Census Level */}
          <div
            style={{
              background: 'white',
              borderRadius: 15,
              padding: 24,
              marginBottom: 20,
              textAlign: 'center',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
            }}
          >
            <h2 style={{ color: '#333', fontSize: 18, marginBottom: 12, fontWeight: 600 }}>
              Census Level
            </h2>

            <div
              style={{
                display: 'inline-block',
                padding: '18px 44px',
                borderRadius: 12,
                background: getCensusColor(status.census_level),
                color: 'white',
                fontSize: isMobile ? 34 : 42,
                fontWeight: 800,
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
              }}
            >
              {status.census_level}
            </div>

            {censusThresholdCards && (
              <div style={{ marginTop: 18 }}>
                <div style={{ fontSize: 13, color: '#666', marginBottom: 10 }}>
                  Mileage thresholds for <strong>{censusThresholdCards.label}</strong> census (max allowable without
                  leadership approval)
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                    gap: 10
                  }}
                >
                  {[1, 2, 3, 4].map((n) => (
                    <div
                      key={n}
                      style={{
                        background: '#f8f9fa',
                        border: '1px solid #eee',
                        borderRadius: 12,
                        padding: 12,
                        textAlign: 'center'
                      }}
                    >
                      <div style={{ fontSize: 12, color: '#666', fontWeight: 800 }}>
                        {n} Ambulance{n > 1 ? 's' : ''}
                      </div>
                      <div style={{ fontSize: 22, fontWeight: 900, color: '#333', marginTop: 6 }}>
                        {getMileageFor(status.census_level, n)}
                        {status.census_level === 'Black' && n === 1 ? '*' : ''} miles
                      </div>
                    </div>
                  ))}
                </div>

                <div style={{ marginTop: 10, fontSize: 12, color: '#777', lineHeight: 1.45 }}>
                  <div>
                    <strong>Long-distance:</strong> Transports over <strong>250 miles</strong> require at least{' '}
                    <strong>24 hours’ notice</strong> and manager approval.
                  </div>
                  <div style={{ marginTop: 6 }}>
                    <strong>*Black/1 ambulance note:</strong> When only one ambulance is in service, ALS/CCT transports
                    may be accepted up to <strong>75 miles</strong> when operationally feasible (even during Purple and
                    Black census levels).
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Shift Information */}
          {(status.shift_7a_7p_units || status.shift_11a_11p_units || status.shift_7p_7a_units) && (
            <div
              style={{
                background: 'white',
                borderRadius: 15,
                padding: 25,
                marginBottom: 12,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
              }}
            >
              <h3 style={{ color: '#333', fontSize: 18, marginBottom: 10, fontWeight: 600 }}>
                Shift Information and Mileage Thresholds
              </h3>
              <div style={{ color: '#777', fontSize: 13, marginBottom: 14 }}>
                Mileage shown reflects the maximum allowable transport distance (without leadership approval) based on
                current census and in-service ambulance count.
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                  gap: 12
                }}
              >
                {shifts.map((s) => {
                  if (!s.raw) return null;

                  const parsed = s.parsed;
                  const total = s.totalAmbulances;
                  const mileage = total ? getMileageFor(status.census_level, total) : null;
                  const totalClamped = total ? Math.max(1, Math.min(4, total)) : null;

                  return (
                    <div
                      key={s.key}
                      style={{
                        background: '#f8f9fa',
                        borderRadius: 12,
                        padding: 14,
                        border: '1px solid #eee'
                      }}
                    >
                      <div style={{ fontWeight: 900, color: '#0E3293', marginBottom: 10 }}>{s.label}</div>

                      {parsed ? (
                        <>
                          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                            <div
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 8,
                                padding: '8px 10px',
                                borderRadius: 10,
                                background: 'white',
                                border: '1px solid #e6e6e6',
                                fontWeight: 900,
                                color: '#333'
                              }}
                            >
                              <span style={{ color: '#0E3293' }}>BLS</span>
                              <span style={{ fontSize: 18 }}>{parsed.bls}</span>
                            </div>
                            <div
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 8,
                                padding: '8px 10px',
                                borderRadius: 10,
                                background: 'white',
                                border: '1px solid #e6e6e6',
                                fontWeight: 900,
                                color: '#333'
                              }}
                            >
                              <span style={{ color: '#0E3293' }}>ALS</span>
                              <span style={{ fontSize: 18 }}>{parsed.als}</span>
                            </div>
                          </div>

                          <div
                            style={{
                              background: 'white',
                              border: '1px solid #e6e6e6',
                              borderRadius: 12,
                              padding: 12
                            }}
                          >
                            <div style={{ fontSize: 12, color: '#666', fontWeight: 800, marginBottom: 4 }}>
                              Total ambulances considered: {total ?? '—'}
                              {s.key === '11a' ? (
                                <span style={{ fontWeight: 600, color: '#999' }}> (7a-7p + 11a-11p)</span>
                              ) : null}
                            </div>

                            {mileage ? (
                              <div style={{ fontSize: 18, fontWeight: 900, color: '#333' }}>
                                Max distance:{' '}
                                <span style={{ color: '#0E3293' }}>
                                  {mileage}
                                  {status.census_level === 'Black' && totalClamped === 1 ? '*' : ''} miles
                                </span>
                              </div>
                            ) : (
                              <div style={{ fontSize: 13, color: '#777' }}>Mileage threshold not available.</div>
                            )}
                          </div>
                        </>
                      ) : (
                        <div style={{ color: '#666', fontSize: 14, lineHeight: 1.5 }}>{s.raw}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div
            style={{
              background: '#eef3fb',
              border: '1px solid #d7e3ff',
              borderRadius: 12,
              padding: '14px 18px',
              marginBottom: 20,
              textAlign: 'center'
            }}
          >
            <p style={{ color: '#0E3293', fontSize: 14, fontWeight: 800, margin: 0 }}>
              ✓ All ALS units are also CCT capable
            </p>
          </div>

          {/* Transport Activity */}
          <div
            style={{
              background: 'white',
              borderRadius: 15,
              padding: 25,
              marginBottom: 20,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
            }}
          >
            <h3 style={{ color: '#333', fontSize: 18, marginBottom: 6, fontWeight: 600 }}>
              Transport Activity
            </h3>

            <div style={{ color: '#777', fontSize: 13, marginBottom: 10 }}>
              Scheduled for today • Cancellations from yesterday
            </div>

            <div
              style={{
                color: '#777',
                fontSize: 12,
                lineHeight: 1.4,
                background: '#f8f9fa',
                border: '1px solid #eee',
                borderRadius: 12,
                padding: 10,
                marginBottom: 14
              }}
            >
              This section is typically updated at the beginning of the day to support planning. It may not change during
              the day to reflect real-time transport volume.
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(2, minmax(200px, 1fr))',
                gap: 15
              }}
            >
              <div
                style={{
                  padding: 15,
                  background: '#f8f9fa',
                  borderRadius: 12,
                  textAlign: 'center',
                  border: '1px solid #eee'
                }}
              >
                <div style={{ fontSize: 32, fontWeight: 900, color: '#333' }}>
                  {status.transports_scheduled || 0}
                </div>
                <div style={{ fontSize: 14, color: '#666', marginTop: 5 }}>Scheduled for today</div>
              </div>

              <div
                style={{
                  padding: 15,
                  background: '#f8f9fa',
                  borderRadius: 12,
                  textAlign: 'center',
                  border: '1px solid #eee'
                }}
              >
                <div style={{ fontSize: 32, fontWeight: 900, color: '#333' }}>
                  {status.cancellations || 0}
                </div>
                <div style={{ fontSize: 14, color: '#666', marginTop: 5 }}>Cancellations (yesterday)</div>
              </div>
            </div>
          </div>

          {/* Notes */}
          {status.notes && (
            <div
              style={{
                background: 'white',
                borderRadius: 15,
                padding: 25,
                marginBottom: 20,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
              }}
            >
              <h3 style={{ color: '#333', fontSize: 18, marginBottom: 15, fontWeight: 600 }}>
                Additional Notes
              </h3>
              <p
                style={{
                  color: '#666',
                  lineHeight: 1.6,
                  whiteSpace: 'pre-wrap',
                  margin: 0
                }}
              >
                {status.notes}
              </p>
            </div>
          )}

          {/* Footer */}
          <div style={{ textAlign: 'center', padding: 20, color: '#999', fontSize: 12 }}>
            <p style={{ marginBottom: 6 }}>
              This page auto-refreshes every 60 seconds (may pause in background tabs)
            </p>
            <p style={{ marginTop: 5 }}>NarcoticTrack © 2025 NarcoticTrack Inventory System, LLC</p>
          </div>
        </div>
      </div>

      {/* PCS Decision Tree Modal */}
      {showPCS && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setShowPCS(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.65)',
            zIndex: 99999,
            display: 'block',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            padding: 'max(16px, env(safe-area-inset-top)) 16px max(16px, env(safe-area-inset-bottom))'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: 760,
              margin: '0 auto',
              background: 'white',
              borderRadius: 16,
              boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
              overflow: 'hidden',
              maxHeight: 'calc(100vh - 32px)',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <div
              style={{
                padding: 16,
                borderBottom: '1px solid #eee',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                position: 'sticky',
                top: 0,
                background: 'white',
                zIndex: 2
              }}
            >
              <div>
                <div style={{ fontSize: 16, fontWeight: 900, color: '#0E3293' }}>
                  Ambulance Qualification (PCS) Screening
                </div>
                <div style={{ fontSize: 12, color: '#777', marginTop: 2 }}>
                  Screening tool only. Do not enter patient identifiers.
                </div>
              </div>

              <button
                onClick={() => setShowPCS(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: 24,
                  cursor: 'pointer',
                  color: '#666',
                  padding: 6,
                  lineHeight: 1
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div style={{ padding: 16, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <div
                style={{
                  background: '#f8f9fa',
                  border: '1px solid #eee',
                  borderRadius: 12,
                  padding: 12,
                  marginBottom: 14,
                  fontSize: 12,
                  color: '#666',
                  lineHeight: 1.45
                }}
              >
                This tool supports quick screening for non-emergent discharge/interfacility transport. A PCS form helps
                document the rationale, but does not guarantee coverage. Coverage depends on medical necessity and
                supporting documentation.
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                  gap: 12
                }}
              >
                {[
                  { key: 'isEmergency', label: 'Is this an emergency situation (unstable/urgent)?' },
                  { key: 'unsafeAltTransport', label: 'Would wheelchair van/POV be unsafe or contraindicated?' },
                  { key: 'cannotSitUpright', label: 'Cannot safely sit upright for the trip (positioning required)' },
                  { key: 'bedConfined', label: 'Bed confined (unable to get up, sit, or ambulate without assistance)' },
                  { key: 'requiresTwoPersonAssist', label: 'Requires two-person assist for transfers/mobility' },
                  { key: 'needsOxygen', label: 'Requires oxygen en route / risk of decomp without oxygen' },
                  { key: 'needsCardiacMonitoring', label: 'Requires monitoring en route (clinical risk without it)' },
                  { key: 'needsIVMedsOrDrips', label: 'IV meds/infusions required during transport' },
                  { key: 'needsAirwaySupport', label: 'Airway support required (suction, advanced airway concerns)' },
                  { key: 'alteredMentalStatus', label: 'AMS/behavioral safety risk without medical transport support' }
                ].map((q) => (
                  <label
                    key={q.key}
                    style={{
                      display: 'flex',
                      gap: 10,
                      alignItems: 'flex-start',
                      padding: 12,
                      borderRadius: 12,
                      border: '1px solid #eee',
                      background: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={pcs[q.key]}
                      onChange={(e) => setPcs((p) => ({ ...p, [q.key]: e.target.checked }))}
                      style={{ marginTop: 3 }}
                    />
                    <span style={{ fontSize: 13, color: '#333', lineHeight: 1.35, fontWeight: 700 }}>
                      {q.label}
                    </span>
                  </label>
                ))}
              </div>

              {pcsResult && (
                <div style={{ marginTop: 14 }}>
                  <div
                    style={{
                      borderRadius: 12,
                      padding: 14,
                      border: '1px solid #eee',
                      background:
                        pcsResult.level === 'green'
                          ? '#e8f5e9'
                          : pcsResult.level === 'yellow'
                          ? '#fff8e1'
                          : pcsResult.level === 'red'
                          ? '#fdecea'
                          : '#f3f4f6'
                    }}
                  >
                    <div style={{ fontWeight: 900, marginBottom: 6, color: '#333' }}>{pcsResult.title}</div>
                    <div style={{ fontSize: 13, color: '#555', lineHeight: 1.45 }}>{pcsResult.summary}</div>
                  </div>

                  <div style={{ marginTop: 10, fontSize: 12, color: '#777', lineHeight: 1.45 }}>
                    Tip: In documentation, clearly state <strong>why other transport is contraindicated</strong> and what{' '}
                    <strong>skilled needs</strong> exist during the trip (positioning/oxygen/monitoring/IV, etc.).
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <button
                  onClick={() =>
                    setPcs({
                      isEmergency: false,
                      bedConfined: false,
                      cannotSitUpright: false,
                      needsOxygen: false,
                      needsCardiacMonitoring: false,
                      needsIVMedsOrDrips: false,
                      needsAirwaySupport: false,
                      unsafeAltTransport: false,
                      requiresTwoPersonAssist: false,
                      alteredMentalStatus: false
                    })
                  }
                  style={{
                    flex: 1,
                    padding: '12px 14px',
                    borderRadius: 12,
                    border: '1px solid #ddd',
                    background: '#fff',
                    fontWeight: 900,
                    cursor: 'pointer'
                  }}
                >
                  Reset
                </button>

                <button
                  onClick={() => setShowPCS(false)}
                  style={{
                    flex: 1,
                    padding: '12px 14px',
                    borderRadius: 12,
                    border: 'none',
                    background: '#0E3293',
                    color: 'white',
                    fontWeight: 900,
                    cursor: 'pointer'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default PublicDailyStatus;
